import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from 'utils/axios';

function UserSDSManager() {
  const navigate = useNavigate();
  const [sdsList, setSDSList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuOpenId, setMenuOpenId] = useState(null);

  useEffect(() => {
    fetchSDSList();
    // Poll for status updates every 10 seconds
    const interval = setInterval(() => {
      fetchSDSList();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchSDSList = async () => {
    try {
      const response = await axios.get('/safetynow/usersds/list');
      setSDSList(response.data);
    } catch (error) {
      console.error('Error fetching SDS list:', error);
      if (error.response?.status !== 401) {
        alert('Failed to load SDS files');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files.length) return;

    setUploading(true);
    const formData = new FormData();
    for (let file of files) {
      formData.append('files', file);
    }

    try {
      await axios.post('/safetynow/usersds/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`${files.length} file(s) uploaded successfully`);
      fetchSDSList();
      
      // Clear the input
      event.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, filename) => {
    if (window.confirm(`Delete ${filename}?`)) {
      try {
        await axios.delete(`/safetynow/usersds/${id}`);
        alert('SDS deleted successfully');
        fetchSDSList();
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete SDS');
      }
    }
  };

  const handleReprocess = async (id) => {
    try {
      await axios.post(`/safetynow/usersds/${id}/reprocess`);
      alert('SDS resubmitted for processing');
      fetchSDSList();
      setMenuOpenId(null);
    } catch (error) {
      console.error('Reprocess error:', error);
      alert('Failed to reprocess SDS');
    }
  };

  const viewOriginal = async (sds) => {
    try {
      const response = await axios.get(`/safetynow/usersds/${sds.id}/original`);
      
      // Decode base64 and create download link
      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.file_type });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      link.click();
      
      window.URL.revokeObjectURL(url);
      console.log('Download started');
    } catch (error) {
      console.error('View original error:', error);
      alert('Failed to download original file');
    }
  };

  const viewProcessed = async (sds) => {
    if (!sds.has_extracted_data) {
      alert('No processed data available yet');
      return;
    }
    
    // Navigate to the SDS datasheet page
    navigate(`/tools/safety-now/sds/${sds.id}`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { 
        bg: 'bg-gray-100', 
        text: 'text-gray-700', 
        label: 'Pending',
        icon: null
      },
      processing: { 
        bg: 'bg-blue-100', 
        text: 'text-blue-700', 
        label: 'Processing',
        icon: '🔄'
      },
      completed: { 
        bg: 'bg-green-100', 
        text: 'text-green-700', 
        label: 'Completed',
        icon: '✓'
      },
      failed: { 
        bg: 'bg-red-100', 
        text: 'text-red-700', 
        label: 'Failed',
        icon: '✗'
      }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {status === 'processing' && (
          <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        {config.icon && status !== 'processing' && <span className="mr-1">{config.icon}</span>}
        {config.label}
      </span>
    );
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-md bg-white/40 rounded-2xl p-8 shadow-xl border border-white/50">
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">My SDS Files</h2>
            <p className="text-gray-600">Manage your uploaded Safety Data Sheets</p>
          </div>
        </div>
        
        <input
          type="file"
          multiple
          onChange={handleUpload}
          className="hidden"
          id="sds-upload-input"
          accept=".pdf,.doc,.docx"
        />
        <label htmlFor="sds-upload-input">
          <span className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer ${
            uploading 
              ? 'bg-gray-400 text-white cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}>
            {uploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" />
                </svg>
                Upload SDS Files
              </>
            )}
          </span>
        </label>
      </div>

      {sdsList.length === 0 ? (
        <div className="text-center py-12 bg-white/50 rounded-xl border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-800">No SDS files uploaded yet</h3>
          <p className="mt-2 text-sm text-gray-600">Upload your first SDS file to get started</p>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white/60 rounded-xl shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50/80">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Filename</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">File Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Upload Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white/40 divide-y divide-gray-200">
              {sdsList.map((sds) => (
                <tr key={sds.id} className="hover:bg-white/60 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm font-medium text-gray-900">{sds.filename}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{formatFileSize(sds.file_size)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {new Date(sds.upload_date).toLocaleDateString()}
                      <div className="text-xs text-gray-500">{new Date(sds.upload_date).toLocaleTimeString()}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(sds.processing_status)}
                    {sds.processing_error && (
                      <div className="text-xs text-red-600 mt-1">{sds.processing_error}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => viewOriginal(sds)}
                        className="text-blue-600 hover:text-blue-900 hover:bg-blue-50 px-3 py-1 rounded transition-colors"
                        title="Download Original"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                      
                      {sds.has_extracted_data && (
                        <button
                          onClick={() => viewProcessed(sds)}
                          className="text-green-600 hover:text-green-900 hover:bg-green-50 px-3 py-1 rounded transition-colors"
                          title="View Processed Data"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                      )}
                      
                      <div className="relative">
                        <button
                          onClick={() => setMenuOpenId(menuOpenId === sds.id ? null : sds.id)}
                          className="text-gray-600 hover:text-gray-900 hover:bg-gray-50 px-3 py-1 rounded transition-colors"
                          title="More Options"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                          </svg>
                        </button>
                        
                        {menuOpenId === sds.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                            <div className="py-1">
                              <button
                                onClick={() => handleReprocess(sds.id)}
                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                🔄 Process Again
                              </button>
                              <button
                                onClick={() => {
                                  handleDelete(sds.id, sds.filename);
                                  setMenuOpenId(null);
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                              >
                                🗑️ Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserSDSManager;
